import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Percent, Plus, Star } from 'lucide-react';
import alertService from '../../utils/alert';
import { formatDate } from '../../utils/formatDate';
import {
  fetchTaxProfiles, createTaxProfile, updateTaxProfile, deleteTaxProfile,
} from '../../redux/crmSlice';
import PageHeader from '../../components/PageHeader';
import {
  PAGE, CARD, TABLE, THEAD, TH, TD, TR, NOTE, INPUT, BTN_PRIMARY,
  Field, Checkbox, RowActions, TableMessage, StatusPill, ListToolbar, FormModal,
} from '../../components/crm/ui';

const BLANK_PROFILE = {
  name: '', country: '', rate: '', isInclusive: false, isDefault: false, isActive: true,
};

function TaxProfileForm({ profile, onClose }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState(() => (profile ? { ...BLANK_PROFILE, ...profile, rate: String(profile.rate) } : BLANK_PROFILE));
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.name.trim()) return alertService.error('Error', 'Tax profile name is required');
    const rate = Number(form.rate);
    if (form.rate === '' || Number.isNaN(rate) || rate < 0 || rate > 100) {
      return alertService.error('Error', 'Rate must be a number between 0 and 100');
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        country: form.country,
        rate,
        isInclusive: form.isInclusive,
        isDefault: form.isDefault,
        isActive: form.isActive,
      };
      if (profile) {
        await dispatch(updateTaxProfile({ id: profile._id, data: payload })).unwrap();
        alertService.success('Updated', 'Tax profile updated');
      } else {
        await dispatch(createTaxProfile(payload)).unwrap();
        alertService.success('Created', 'Tax profile created');
      }
      onClose();
    } catch (err) {
      alertService.error('Error', err || 'Failed to save tax profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      title={profile ? `Edit ${(profile.name || '').trim()}` : 'New tax profile'}
      onClose={onClose}
      onSubmit={save}
      saving={saving}
      submitLabel="Save tax profile"
      size="max-w-2xl"
    >
      <p className={NOTE}>
        Define your own tax rule — official rates vary by country and product and change over time,
        so WaapLeads doesn't assert them for you. Enter the rate that applies to your business.
      </p>
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Name *">
          <input aria-label="Name" className={INPUT} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. UK VAT, UAE VAT, US Sales Tax — CA" />
        </Field>
        <Field label="Country">
          <input aria-label="Country" className={INPUT} value={form.country} onChange={(e) => set({ country: e.target.value })} placeholder="e.g. United Kingdom" />
        </Field>
        <Field label="Rate (%) *">
          <input aria-label="Rate (%)" type="number" min="0" max="100" step="0.01" className={INPUT} value={form.rate} onChange={(e) => set({ rate: e.target.value })} placeholder="e.g. 20" />
        </Field>
        <Field label="Tax behavior">
          <select aria-label="Tax behavior" className={INPUT} value={form.isInclusive ? 'inclusive' : 'exclusive'} onChange={(e) => set({ isInclusive: e.target.value === 'inclusive' })}>
            <option value="exclusive">Exclusive — tax added on top of the price</option>
            <option value="inclusive">Inclusive — price already includes tax</option>
          </select>
        </Field>
      </section>
      <div className="flex items-center gap-6">
        <Checkbox label="Set as default" checked={form.isDefault} onChange={(v) => set({ isDefault: v })} />
        <Checkbox label="Active" checked={form.isActive} onChange={(v) => set({ isActive: v })} />
      </div>
    </FormModal>
  );
}

export default function TaxProfilesPage() {
  const dispatch = useDispatch();
  const { taxProfiles } = useSelector((state) => state.crm);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | profile
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(fetchTaxProfiles()).finally(() => setLoading(false));
  }, [dispatch]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return taxProfiles.filter((p) => {
      if (status === 'active' && !p.isActive) return false;
      if (status === 'inactive' && p.isActive) return false;
      return !q || p.name.toLowerCase().includes(q) || (p.country || '').toLowerCase().includes(q);
    });
  }, [taxProfiles, status, search]);

  const handleDelete = async (profile) => {
    const confirmed = await alertService.confirm('Delete Tax Profile?', `Permanently delete "${profile.name}"?`, 'danger', 'Delete');
    if (!confirmed) return;
    try {
      await dispatch(deleteTaxProfile(profile._id)).unwrap();
      alertService.success('Deleted', 'Tax profile deleted');
    } catch {
      alertService.error('Error', 'Failed to delete tax profile');
    }
  };

  return (
    <div className={PAGE}>
      <PageHeader
        title="Tax Profiles"
        icon={Percent}
        subtitle="VAT, sales tax or GST rules you can apply on invoices, for any country. India GST invoices use the built-in CGST/SGST/IGST unless a profile is selected."
        actions={<button type="button" onClick={() => setEditing('new')} className={BTN_PRIMARY}><Plus size={15} /> New Tax Profile</button>}
      />

      <div className={CARD}>
        <ListToolbar
          tabs={[['', 'All'], ['active', 'Active'], ['inactive', 'Inactive']]}
          value={status}
          onChange={setStatus}
          search={search}
          onSearch={setSearch}
          placeholder="Search name or country…"
        />
        <div className="overflow-x-auto">
          <table className={`${TABLE} min-w-[47.5rem]`}>
            <thead className={THEAD}>
              <tr className="border-b border-[#D8DDE6]">
                <th className={TH}>Name</th>
                <th className={TH}>Country</th>
                <th className={`${TH} text-right`}>Rate</th>
                <th className={TH}>Behavior</th>
                <th className={TH}>Status</th>
                <th className={TH}>Updated</th>
                <th className={TD} />
              </tr>
            </thead>
            <tbody>
              <TableMessage
                colSpan={7}
                loading={loading}
                show={loading || rows.length === 0}
                empty={taxProfiles.length === 0 ? 'No tax profiles yet. Add the tax rules you apply on invoices.' : 'No tax profiles match your filters.'}
              />
              {!loading && rows.map((profile) => (
                <tr key={profile._id} className={TR}>
                  <td className={TD}>
                    {/* The flex layout lives on an inner span — a flex <td> breaks the row's alignment. */}
                    <span className="inline-flex items-center gap-1.5 font-semibold text-[#2E2E2E]">
                      {profile.isDefault && <Star size={12} className="text-[#FE9339] fill-[#FE9339]" aria-label="Default" />}
                      {profile.name}
                    </span>
                  </td>
                  <td className={`${TD} text-[#706E6B]`}>{profile.country || '—'}</td>
                  <td className={`${TD} text-right tabular-nums font-semibold`}>{profile.rate}%</td>
                  <td className={`${TD} text-[#706E6B]`}>{profile.isInclusive ? 'Inclusive' : 'Exclusive'}</td>
                  <td className={TD}><StatusPill active={profile.isActive} /></td>
                  <td className={`${TD} text-[#706E6B]`}>{formatDate(profile.updatedAt)}</td>
                  <RowActions name={(profile.name || '').trim()} onEdit={() => setEditing(profile)} onDelete={() => handleDelete(profile)} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <TaxProfileForm profile={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}
